import React from 'react'

import { Outlet, useLocation, useNavigationType } from 'react-router-dom'

import EmptyState from '@/components/base/emptyState'
import { WifiOff } from 'lucide-react'
import AnimatedWrapper from '@/components/base/animatedWrapper'
import { AppContext } from '@/components/base/appcontext'
import BottomNav from '@/components/base/navigation'
import { AppShellSkeleton } from '@/components/base/appShellSkeleton'
import { NavigationProgress } from '@/components/base/navigationProgress'
import { warmRouteChunks } from '@/lib/routeChunks'
import { normalizeEntryUrl, recordHistoryEntry } from '@/lib/historyEntries'
import Loading from '@/components/base/loading'
const Onboarding = React.lazy(() => import('@/routes/onboarding'))
import { ONBOARDING_COMPLETED_KEY } from '@/components/onboarding/fullscreen'
import { authApiClient } from '@/lib/api/lib/client'
import { isUnauthorizedFailure } from '@/lib/api/errors'
import { recordVisit } from '@/lib/visit'

// One-time migration for users who finished onboarding before the
// 'onboarding.completed' flag existed — for them the consent key was the
// gate. Guarded by a marker so it runs only on the first load after the
// gate change: without the guard, a NEW user's consent click (written
// before form submission) would be mistaken for completion on reload.
const ONBOARDING_GATE_MIGRATED_KEY = 'onboarding.gate.v2'
try {
    if (!localStorage.getItem(ONBOARDING_GATE_MIGRATED_KEY)) {
        if (
            !localStorage.getItem(ONBOARDING_COMPLETED_KEY) &&
            localStorage.getItem('onboarding.consent.toc')
        ) {
            localStorage.setItem(ONBOARDING_COMPLETED_KEY, 'legacy')
        }
        localStorage.setItem(ONBOARDING_GATE_MIGRATED_KEY, '1')
    }
} catch {
    // Safari private mode can throw on localStorage access
}

const NAV_HIDDEN_PATTERNS = [
    /^\/programmes\/[^/]+\/edit$/,
    /^\/programmes\/[^/]+\/start$/,
    /^\/schedule\/[^/]+\/details$/,
    /^\/schedule\/[^/]+\/[^/]+\/reflect$/,
    /^\/communityProgrammes\/[^/]+\/details$/
]

function RetryScreen({ onRetry }: { onRetry: () => void }) {
    const appContext = React.useContext(AppContext)

    return (
        <EmptyState
            className="h-screen w-screen"
            kind="unavailable"
            icon={WifiOff}
            title={appContext.geti18n('base.static.unavailable.title')}
            action={{
                label: appContext.geti18n('base.static.retry.text'),
                onClick: onRetry
            }}
        />
    )
}

function Root() {
    const appContext = React.useContext(AppContext)
    const location = useLocation()
    const navigationType = useNavigationType()

    // Back controls step through real history when the entry behind is
    // where they mean to go (useReferrerBack).
    React.useEffect(() => {
        recordHistoryEntry(
            normalizeEntryUrl(location.pathname, location.search),
            navigationType === 'PUSH'
        )
    }, [location.key, location.pathname, location.search, navigationType])

    let completed: string | null = null
    try {
        completed = localStorage.getItem(ONBOARDING_COMPLETED_KEY)
    } catch {
        // Safari private mode can throw on localStorage access
    }

    // Telegram webviews drop localStorage regularly (cache clears, iOS
    // eviction, another device), and Safari private mode blocks writes
    // entirely — so a missing local flag does not mean the user is new.
    // Before forcing the onboarding flow again, ask the backend: a set
    // tocConcent means the profile was already submitted.
    const [serverGate, setServerGate] = React.useState<
        'checking' | 'completed' | 'incomplete' | 'unavailable'
    >('checking')
    const [gateAttempt, setGateAttempt] = React.useState(0)
    React.useEffect(() => {
        if (appContext.loading || appContext.staticError || completed) return
        let cancelled = false
        authApiClient
            .me()
            .then((profile) => {
                if (cancelled) return
                if (profile?.tocConcent && profile.tocConcent > 0) {
                    try {
                        localStorage.setItem(ONBOARDING_COMPLETED_KEY, 'server')
                    } catch {
                        // Storage blocked — serverGate keeps this session going
                    }
                    setServerGate('completed')
                } else {
                    setServerGate('incomplete')
                }
            })
            .catch((error) => {
                if (cancelled) return
                // A rejected /me only proves the user is new when the backend
                // said so. An outage or a dropped connection must not push a
                // returning user back through onboarding.
                setServerGate(
                    isUnauthorizedFailure(error) ? 'incomplete' : 'unavailable'
                )
            })
        return () => {
            cancelled = true
        }
    }, [appContext.loading, appContext.staticError, completed, gateAttempt])

    // Past onboarding, opening the app is a visit (active on the boards).
    const ready =
        !appContext.loading &&
        !appContext.staticError &&
        (!!completed || serverGate === 'completed')
    React.useEffect(() => {
        if (!ready) return
        recordVisit()
        // The first screen is up: fetch the other screens' code while idle.
        warmRouteChunks()
    }, [ready])

    const retryServerGate = React.useCallback(() => {
        setServerGate('checking')
        setGateAttempt((attempt) => attempt + 1)
    }, [])

    if (appContext.staticError) {
        return <RetryScreen onRetry={appContext.retryStatic} />
    }

    if (appContext.loading || (!completed && serverGate === 'checking')) {
        return <AppShellSkeleton />
    }

    if (!completed && serverGate === 'unavailable') {
        return <RetryScreen onRetry={retryServerGate} />
    }

    if (!completed && serverGate === 'incomplete') {
        return (
            <div className="flex flex-col h-screen w-screen justify-center">
                <AnimatedWrapper animationName="fadeIn" duration={500}>
                    <React.Suspense fallback={<Loading />}>
                        <Onboarding />
                    </React.Suspense>
                </AnimatedWrapper>
            </div>
        )
    }

    const hideNav = NAV_HIDDEN_PATTERNS.some((p) => p.test(location.pathname))

    return (
        <div className="flex flex-col min-h-screen w-full">
            <NavigationProgress />
            <Outlet />
            {!hideNav && <BottomNav />}
        </div>
    )
}

export default Root
